import { DateUtils } from './date-utils';
import { BookingUtils } from './booking-utils';
import { ExpertTime, SalonDate } from '../models/booking';

export interface IcsEventInput {
  salonDate: SalonDate;
  expertTime: ExpertTime;
  bookingId: string;
  activityName: string;
  salonName: string;
  expertName: string;
  guestName: string;
  guestPhone: string;
  guestEmail: string;
  durationMinutes: number;
  withLabel: string;
  guestLabel: string;
  phoneLabel: string;
  timezone?: string;
}

export class AddToCalendarUtils {
  public static generateIcs(input: IcsEventInput): string {
    const { start, end } = AddToCalendarUtils.computeStartEnd(input);
    const description = AddToCalendarUtils.buildDescription(input);

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//SmallSalon//Booking//EN',
      'BEGIN:VEVENT',
      `UID:${input.bookingId}@smallsalon`,
      `DTSTART:${AddToCalendarUtils.toIcsDateTime(start)}`,
      `DTEND:${AddToCalendarUtils.toIcsDateTime(end)}`,
      `SUMMARY:${input.activityName}${input.salonName ? ' @ ' + input.salonName : ''}`,
      `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
      `ORGANIZER;CN=${input.salonName}:MAILTO:noreply@smallsalon.io`,
      `ATTENDEE;CN=${input.guestName}:MAILTO:${input.guestEmail}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
  }

  public static generateGoogleCalendarUrl(input: IcsEventInput): string {
    const { start, end } = AddToCalendarUtils.computeStartEnd(input);
    const title = `${input.activityName}${input.salonName ? ' @ ' + input.salonName : ''}`;
    const description = AddToCalendarUtils.buildDescription(input);

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${AddToCalendarUtils.toIcsDateTime(start)}/${AddToCalendarUtils.toIcsDateTime(end)}`,
      details: description,
    });
    if (input.salonName) {
      params.set('location', input.salonName);
    }
    if (input.timezone) {
      params.set('ctz', input.timezone);
    }

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  public static toIcsDateTime(d: Date): string {
    return DateUtils.formatDateYYYYMMDD(d) + 'T' + String(d.getHours()).padStart(2, '0') + String(d.getMinutes()).padStart(2, '0') + '00';
  }

  private static computeStartEnd(input: IcsEventInput): { start: Date; end: Date } {
    const start = new Date(BookingUtils.toBookingLocalDateTimeString(input.salonDate, input.expertTime));
    const end = new Date(start.getTime() + input.durationMinutes * 60 * 1000);
    return { start, end };
  }

  private static buildDescription(input: IcsEventInput): string {
    return [
      input.expertName ? `${input.withLabel} ${input.expertName}` : '',
      input.guestName ? `${input.guestLabel} ${input.guestName}` : '',
      input.guestPhone ? `${input.phoneLabel} ${input.guestPhone}` : '',
      `Ref: ${input.bookingId}`,
    ]
      .filter(Boolean)
      .join('\n');
  }
}
